import { useTaskTranslation } from '../hooks/useTaskTranslation';
import type { ReactNode } from 'react';
import { TaskLoadingIndicator } from './TaskLoadingIndicator';
import classes from './TaskPersonPicker.module.css';

interface PersonOption {
  key: string;
  name: string;
  details: string;
  disabled: boolean;
}

interface TaskPersonPickerProps {
  label: string;
  searchLabel: string;
  searchPlaceholder: string;
  listLabel: string;
  search: string;
  onSearchChange: (value: string) => void;
  options: PersonOption[];
  showDetails?: boolean;
  selectedKey: string | null;
  selectedName?: string;
  onSelect: (key: string) => void;
  onClear: () => void;
  clearLabel?: string;
  disabled?: boolean;
  loading: boolean;
  error?: string;
  onRetry: () => void;
  emptyMessage: string;
  children?: ReactNode;
}

export const TaskPersonPicker = ({
  label,
  searchLabel,
  searchPlaceholder,
  listLabel,
  search,
  onSearchChange,
  options,
  showDetails = true,
  selectedKey,
  selectedName,
  onSelect,
  onClear,
  clearLabel,
  disabled = false,
  loading,
  error,
  onRetry,
  emptyMessage,
  children,
}: TaskPersonPickerProps) => {
  const { translations, taskText } = useTaskTranslation();
  return (
    <fieldset
      disabled={disabled}
      className={classes.container}
      aria-label={taskText('task_board.label_details', { label })}
    >
      <label className={classes.field}>
        {label}
        <input
          type="search"
          placeholder={searchPlaceholder}
          aria-label={searchLabel}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </label>
      {selectedKey != null && selectedName !== undefined && (
        <div className={classes.selected}>
          <span>{taskText('task_board.selected_name', { name: selectedName })}</span>
          <button type="button" className={classes.clearSelection} onClick={onClear}>
            {clearLabel ?? translations('task_board.clear_selection')}
          </button>
        </div>
      )}
      {children}
      <div className={classes.list} role="group" aria-label={listLabel} aria-busy={loading}>
        {loading ? (
          <TaskLoadingIndicator />
        ) : error ? (
          <div role="alert" className={classes.empty}>
            {error}{' '}
            <button type="button" onClick={onRetry}>
              {translations('task_board.retry')}
            </button>
          </div>
        ) : (
          <>
            {options.map((option) => (
              <button
                type="button"
                className={classes.option}
                key={option.key}
                aria-pressed={selectedKey === option.key}
                disabled={disabled || loading || option.disabled}
                onClick={() => onSelect(option.key)}
              >
                <span>
                  <strong>{option.name}</strong>
                  {showDetails && <small>{option.details}</small>}
                </span>
              </button>
            ))}
            {!options.length && <div className={classes.empty}>{emptyMessage}</div>}
          </>
        )}
      </div>
    </fieldset>
  );
};
